import { useEffect, useState } from "react";
import { Building2 } from "lucide-react";

import { Button } from "../atoms/Button";
import { Drawer } from "./Drawer";
import { Input } from "../atoms/Input";
import type { AuthorizationBusinessUnit } from "../../types/authorizationAdmin";

const BU_DESCRIPTION_MAX_LEN = 100;

type BusinessUnitDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly editingBu?: AuthorizationBusinessUnit | null;
  /** Used to block duplicate display names (case-insensitive, trim) before API call. */
  readonly existingBusinessUnits?: readonly AuthorizationBusinessUnit[];
  readonly onCreate: (payload: {
    code: string;
    name: string;
    description?: string;
    is_active: boolean;
  }) => Promise<void>;
  readonly onUpdate: (
    businessUnitId: string,
    payload: { name: string; description: string | null; is_active: boolean }
  ) => Promise<void>;
};

export const BusinessUnitDrawer = ({
  isOpen,
  onClose,
  editingBu,
  existingBusinessUnits = [],
  onCreate,
  onUpdate
}: BusinessUnitDrawerProps) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (editingBu) {
      setCode(editingBu.code);
      setName(editingBu.name);
      setDescription((editingBu.description ?? "").slice(0, BU_DESCRIPTION_MAX_LEN));
      setIsActive(editingBu.is_active);
    } else {
      setCode("");
      setName("");
      setDescription("");
      setIsActive(true);
    }
  }, [isOpen, editingBu]);

  const isEdit = Boolean(editingBu);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Business Unit" : "Add Business Unit"}
      headerIcon={<Building2 className="h-5 w-5" />}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="bu-code" className="form-label">
            BU Code <span className="form-label-asterisk">*</span>
          </label>
          {isEdit ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">{code}</p>
          ) : (
            <Input id="bu-code" value={code} onChange={(e) => setCode(e.target.value)} />
          )}
        </div>
        <div className="space-y-1.5">
          <label htmlFor="bu-name" className="form-label">
            BU Name <span className="form-label-asterisk">*</span>
          </label>
          <Input id="bu-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="bu-description" className="form-label">
            Description <span className="font-normal text-gray-500">(max {BU_DESCRIPTION_MAX_LEN} characters)</span>
          </label>
          <Input
            id="bu-description"
            value={description}
            maxLength={BU_DESCRIPTION_MAX_LEN}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Active
          </label>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={isSaving}
            onClick={async () => {
              if ((!isEdit && !code.trim()) || !name.trim()) {
                setError("Code and name are required.");
                return;
              }
              const nameKey = name.trim().toLowerCase();
              const nameConflict = existingBusinessUnits.some(
                (b) =>
                  b.name.trim().toLowerCase() === nameKey && (!editingBu || b.id !== editingBu.id)
              );
              if (nameConflict) {
                setError("A business unit with this name already exists.");
                return;
              }
              setError(null);
              setIsSaving(true);
              try {
                if (editingBu) {
                  await onUpdate(editingBu.id, {
                    name: name.trim(),
                    description: description.trim() === "" ? null : description.trim(),
                    is_active: isActive
                  });
                } else {
                  await onCreate({
                    code: code.trim(),
                    name: name.trim(),
                    description: description.trim() || undefined,
                    is_active: isActive
                  });
                }
                onClose();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Unable to save business unit.");
              } finally {
                setIsSaving(false);
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
